import React, {useState, useMemo, useEffect} from 'react';
import {Navigate, useParams, useLoaderData} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import SvgChartMatrix from '../../components/svgCharts/SvgChartMatrix';
import SvgChartParliament from '../../components/svgCharts/SvgChartParliament';
import ContentSection from '../../components/common/ContentSection/ContentSection';
import VoteBar from '../../components/common/VoteBar/VoteBar';
import StatChip from '../../components/common/StatChip/StatChip';
import {capitalizeFirstLetter, useFormatters} from '../../utils/texts';
import './Home.css';
import AnalysesNavList from '../../components/analysesNav/AnalysesNavList';
import MemberCard from '../../components/common/MemberCard/MemberCard';
import useDimensions from '../../hooks/useDimensions';
import {MeetingCard} from '../../components/meetings/MeetingCard';
import type {PartiesData, SessionData, VoteBasicData} from '../../api';
import {useAppContext} from '../../context/AppContext';
import {SummaryItemData} from '../../types';
import {Dropdown} from 'primereact/dropdown'
import {useSearchParams} from 'react-router-dom';
import {useLocalizationLabels} from '../../context/LocalizationLabelContext';

// Should be done on BE
const getMembers = (partiesData: PartiesData[], leaderName: string) => {
    return partiesData
        .flatMap((party) =>
            party.clenove
                .filter((member) => member.aktivni === 1)
                .map((member) => ({
                    id: member.id,
                    firstName: member.jmeno.split(' ')[0],
                    lastName: member.jmeno.split(' ')[1],
                    partyName: party.strana_zkratka,
                    partyColor: party.strana_barva,
                    gender: member.pohlavi as 0 | 1, // API documents pohlavi as 0=male,1=female
                    leader: member.jmeno === leaderName,
                    photo: member.photo ?? undefined,
                    country: member.stat
                }))
        )
        .sort((a, b) => {
            const lastNameComparison = a.lastName.localeCompare(b.lastName, 'cs');
            if (lastNameComparison !== 0) return lastNameComparison;
            return a.firstName.localeCompare(b.firstName, 'cs');
        });
};

function Home() {
    const {bodyType} = useParams();
    const {t, i18n} = useTranslation(['home', 'translation', 'meetings', 'votings']);
    const currentLocale = i18n.language;
    const formatter = useFormatters(currentLocale);
    const {width} = useDimensions();
    const {basicData, municipalityData: municipality} = useAppContext();

    const response = useLoaderData() as [PartiesData[], PartiesData[], VoteBasicData, SessionData[]];
    const partiesData = response[0];
    const legalPartiesData = response[1];
    const votesData = response[2];
    const meetingsData = response[3];

    const [searchParams, setSearchParams] = useSearchParams();
    const selectedCountry = searchParams.get('country');
    const membersIncrement = width < 1200 ? 9 : 20;
    const [visibleMembersCount, setVisibleMembersCount] = useState(membersIncrement);
    const [politicalView, setPoliticalView] = useState('subjects');

    useEffect(() => {
        if (!selectedCountry) {
            setPoliticalView('subjects');
        }
    }, [selectedCountry]);

    const allMembers = useMemo(() => getMembers(partiesData, basicData?.leader), [partiesData, basicData]);

    const availableCountries = useMemo(() =>
            Array.from(new Set(allMembers.map(m => m.country).filter(Boolean))).sort()
        , [allMembers]);

    const hasCoalition = !["ep", "edmonton"].includes(municipality.text_id); // TODO: To split or dont split between "opozice a koalice" -> Should be done on BE
    const epView = ["ep"].includes(municipality.text_id); // TODO: For ep names on home page like "Frakce" -> Should be done on BE

    const {
        organOptionLabels,
        organNameOptionLabels,
    } = useLocalizationLabels();

    const organID = bodyType ? municipality.organy[bodyType]?.id : undefined;
    const organNamelabel = capitalizeFirstLetter(organID ? organNameOptionLabels[organID]?.label ?? bodyType : '');

    const handleCountryChange = (value: string | null) => {
        setSearchParams((prev) => {
            if (value) {
                prev.set('country', value);
            } else {
                prev.delete('country');
            }
            return prev;
        }, {replace: true});

        setVisibleMembersCount(membersIncrement);
    };

    // Filter for subjects
    const filtredPartiesData = useMemo(() => {
        if (!partiesData || !basicData?.nejnovejsi_zasedani) return [];

        return partiesData.map((party) => {
            const filteredPartyMembers = !selectedCountry
                ? party.clenove
                : party.clenove.filter(m => m.stat === selectedCountry);

            const active_party = basicData.nejnovejsi_zasedani.mandaty.find(
                (obj: { strana_id: number }) => obj.strana_id === party.strana_id
            );

            if (!active_party) {
                return {...party, clenove: filteredPartyMembers, aktivni: false, pocet_mandatu: 0};
            }

            const mandatyCount = filteredPartyMembers.filter(m => m.aktivni === 1).length;

            return {
                ...party,
                clenove: filteredPartyMembers,
                aktivni: mandatyCount > 0,
                pocet_mandatu: mandatyCount
            };
        });
    }, [partiesData, basicData, selectedCountry]);

    // Filter for parties
    const filtredLegalPartiesData = useMemo(() => {
        if (!legalPartiesData || !basicData?.nejnovejsi_zasedani) return [];

        return legalPartiesData.map((party) => {
            const filteredPartyMembers = !selectedCountry
                ? party.clenove
                : party.clenove.filter(m => m.stat === selectedCountry);

            const active_party = basicData.nejnovejsi_zasedani.legalni_mandaty.find(
                (obj: { strana_id: number }) => obj.strana_id === party.strana_id
            );

            if (!active_party) {
                return {...party, clenove: filteredPartyMembers, aktivni: false, pocet_mandatu: 0};
            }

            const mandatyCount = filteredPartyMembers.filter(m => m.aktivni === 1).length;

            return {
                ...party,
                clenove: filteredPartyMembers,
                aktivni: mandatyCount > 0,
                pocet_mandatu: mandatyCount
            };
        });
    }, [legalPartiesData, basicData, selectedCountry]);

    // Filter for members
    const filteredMembers = useMemo(() =>
            !selectedCountry ? allMembers : allMembers.filter(m => m.country === selectedCountry)
        , [selectedCountry, allMembers]);

    const chartStartVersion = filteredMembers.length > 100 ? 2 : 1;
    const [chartVersion, setChartVersion] = useState(chartStartVersion);

    // Basic data transformation
    const filteredBasicData = useMemo(() => {
        if (!basicData) return undefined;
        if (!selectedCountry) return basicData;

        const totalSeats = filteredMembers.length;
        const sortByMandates = (a: any, b: any) => b.pocet_mandatu - a.pocet_mandatu;
        return {
            ...basicData,
            pocet_zastupitelek: filteredMembers.filter(m => Number(m.gender) === 1).length,
            pocet_zastupitelu: filteredMembers.filter(m => Number(m.gender) === 0).length,
            nejnovejsi_zasedani: {
                ...basicData.nejnovejsi_zasedani,
                clenu: totalSeats,
                celkem: totalSeats,
                mandaty: filtredPartiesData
                    .filter(p => p.pocet_mandatu > 0)
                    .map(p => ({
                        strana_id: p.strana_id,
                        strana_zkratka: p.strana_zkratka,
                        strana_barva: p.strana_barva,
                        pocet_mandatu: p.pocet_mandatu
                    })).sort(sortByMandates),
                legalni_mandaty: filtredLegalPartiesData
                    .filter(p => p.pocet_mandatu > 0)
                    .map(p => ({
                        strana_id: p.strana_id,
                        strana_zkratka: p.strana_zkratka,
                        strana_barva: p.strana_barva,
                        pocet_mandatu: p.pocet_mandatu
                    })).sort(sortByMandates)
            }
        };
    }, [selectedCountry, basicData, filteredMembers, filtredPartiesData, filtredLegalPartiesData]);

    // Check
    if (!basicData || !municipality || !partiesData || !votesData || !filteredBasicData) {
        return <Navigate to='/'/>;
    }

    const countryOptions = availableCountries
        .map(country => ({
            label: t('States.' + country, {ns: 'translation'}),
            value: country
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const organ = bodyType ? municipality.organy[bodyType] : undefined;
    const hasLatestMeeting = !!basicData.nejnovejsi_zasedani?.id;
    const leaders = basicData.leaders || [];
    const leader = allMembers.find((member) => member.leader);
    const politicalViewButton = (legalPartiesData.length > 0 && filteredMembers.length <= 200) ? 1 : '';
    const countryFilterButton = availableCountries.length > 0 ? 1 : '';

    const style = getComputedStyle(document.body);
    const voteOutcomeColors = { ACCEPTED: style.getPropertyValue('--clr-1'), REJECTED: style.getPropertyValue('--clr-2') };
    const voteOutcomeItems = [
        { id: 1, code: 'ACCEPTED', count: votesData.pocet_prijatych },
        { id: 2, code: 'REJECTED', count: votesData.pocet_neprijatych }
    ];
    const getVoteOutcomeLabel = (item: { code: string }) =>
        capitalizeFirstLetter(t(item.code === 'ACCEPTED' ? 'approvedProposals' : 'rejectedProposals', {ns: 'votings'}));

    const totalDecidedVotes = votesData.pocet_prijatych + votesData.pocet_neprijatych;
    const acceptedPercent = totalDecidedVotes > 0 ? (votesData.pocet_prijatych / totalDecidedVotes) * 100 : 0;

    const zastupitelkyCelkem = basicData.pocet_zastupitelek_celkem;
    const zastupiteleCelkem = basicData.pocet_zastupitelu_celkem;

    const summaryItems: SummaryItemData[] = [
        {
            icon: 'female',
            text: (t('councilMember_1', {count: filteredBasicData.pocet_zastupitelek})),
            value: filteredBasicData.pocet_zastupitelek,
            mutedText: (!selectedCountry && zastupitelkyCelkem > filteredBasicData.pocet_zastupitelek)
                ? `(${t('x-outOf-y', {ns: 'translation', x: filteredBasicData.pocet_zastupitelek, y: zastupitelkyCelkem})})`
                : undefined
        },
        {
            icon: 'male',
            text: (t('councilMember_0', {count: filteredBasicData.pocet_zastupitelu})),
            value: filteredBasicData.pocet_zastupitelu,
            mutedText: (!selectedCountry && zastupiteleCelkem > filteredBasicData.pocet_zastupitelu)
                ? `(${t('x-outOf-y', {ns: 'translation', x: filteredBasicData.pocet_zastupitelu, y: zastupiteleCelkem})})`
                : undefined
        },
        {
            icon: 'event_available',
            text: (t('meetings', {ns: 'meetings'})),
            value: formatter.number.format(basicData.pocet_zasedani)
        },
        {
            icon: 'select_check_box',
            text: t('votings', {ns: 'votings'}),
            value: formatter.number.format(basicData.pocet_hlasovani)
        }
    ];

    const chartBlock = (
        <div className="chart-container card elevation-1 no-hover dashboard-card">
            <div className="controls-header">
                {countryFilterButton && (
                    <div className="country-filter-wrapper">
                        <Dropdown
                            value={selectedCountry}
                            options={countryOptions}
                            onChange={(e) => handleCountryChange(e.value)}
                            optionLabel="label"
                            optionValue="value"
                            filter
                            placeholder={t('allCountries', {ns: 'translation'})}
                            className="dropdown-custom-style"
                            showClear
                        />
                    </div>
                )}

                <div className="chart-toggle">
                    <button onClick={() => setChartVersion(1)} className={chartVersion === 1 ? 'active' : ''}>
                        {t('svgCharts.matrixChart', {ns: 'translation'})}
                    </button>
                    <button onClick={() => setChartVersion(2)} className={chartVersion === 2 ? 'active' : ''}>
                        {t('svgCharts.parliamentChart', {ns: 'translation'})}
                    </button>
                </div>

                {politicalViewButton && (
                    <div className="segmented-control-wrapper">
                        <div className="segmented-control">
                            <button className={politicalView === 'subjects' ? 'active' : ''}
                                    onClick={() => setPoliticalView('subjects')}>
                                {t('svgCharts.subjectView', {ns: 'translation'})}
                            </button>
                            <button className={politicalView === 'parties' ? 'active' : ''}
                                    onClick={() => setPoliticalView('parties')}>
                                {t('svgCharts.partyView', {ns: 'translation'})}
                            </button>
                            <button className={politicalView === 'combined' ? 'active' : ''}
                                    onClick={() => setPoliticalView('combined')}>
                                {t('svgCharts.combinedView', {ns: 'translation'})}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {chartVersion === 1 ? (
                <SvgChartMatrix
                    key={`matrix-${selectedCountry}-${politicalView}`}
                    basicData={filteredBasicData}
                    partiesData={filtredPartiesData}
                    legalPartiesData={filtredLegalPartiesData}
                    coalitionView={hasCoalition}
                    epView={epView}
                    politicalView={politicalView}
                />
            ) : (
                <SvgChartParliament
                    key={`parliament-${selectedCountry}-${politicalView}`}
                    basicData={filteredBasicData}
                    partiesData={filtredPartiesData}
                    legalPartiesData={filtredLegalPartiesData}
                    coalitionView={hasCoalition}
                    epView={epView}
                    politicalView={politicalView}
                />
            )}
        </div>
    );

    return (
        <div className="page-dashboard home-dashboard">
            <div className="page-header-row">
                <ContentSection title={organNamelabel} level={1} subtitle={basicData['funkcni_obdobi']} className='page-title-section'/>
                <div className="stat-chip-row">
                    {summaryItems.map((item, index) => (
                        <StatChip key={index} icon={item.icon} value={item.value} label={item.text} mutedText={item.mutedText} />
                    ))}
                </div>
            </div>
            <div className='page-description'>{t('overview')}</div>

            {chartBlock}

            {hasLatestMeeting && (
                <>
                    <ContentSection
                        title={t('votings', {ns: 'votings'})}
                        subtitle={t('votingsTooltip')}
                        className='votings-card dashboard-card'
                        contentClassName='votings-section-content'
                        titleElement={
                            <div className='votings-summary-wrapper'>
                                <div className='votings-summary-item'>
                                    <h3><span
                                        className='material-symbols-rounded'>stacks</span>{formatter.number.format(basicData.pocet_hlasovani)}
                                    </h3>
                                    <p>{t('validVotings', {ns: 'votings'})}</p>
                                </div>
                                <div className='votings-summary-item'>
                                    <h3><span
                                        className='material-symbols-rounded'>select_check_box</span>{formatter.number.format(basicData['pocet_hlasovani_clenu'])}
                                    </h3>
                                    <p>{t('allMembersVotes')}</p>
                                </div>
                            </div>
                        }
                        showMoreChevronLinkProps={{text: t('showVotings'), link: 'hlasovani'}}
                    >
                        <div className='votings-headline'>
                            <b>{formatter.percentFrom100(acceptedPercent)}</b>
                            <span>{t('approvedProposals', {ns: 'votings'})}</span>
                        </div>
                        <VoteBar
                            items={voteOutcomeItems}
                            colors={voteOutcomeColors}
                            getLabel={getVoteOutcomeLabel}
                            formatPercent={formatter.percentFrom100}
                        />
                    </ContentSection>

                    <div className="dashboard-grid">
                        <ContentSection
                            title={(t('meetings', {ns: 'meetings'}))}
                            subtitle={`${t('inTerm', {
                                ns: 'meetings',
                                num: meetingsData.length,
                                where: organ ? organ.misto : '',
                                type: bodyType ? organOptionLabels[bodyType]?.label_genitiv ?? bodyType : ''
                            })} ${t('moreMeetings')}`}
                            className='dashboard-card'
                            contentClassName='meeting-row-list'
                            showMoreChevronLinkProps={{text: t('showMeetings'), link: 'zasedani'}}
                        >
                            {meetingsData.slice(0, 3).map((meeting, index) => (
                                <MeetingCard key={index} meetingData={meeting} compact/>
                            ))}
                        </ContentSection>

                        <ContentSection title={t('analysesTitle', {ns: 'analyses'})}
                                 subtitle={t('analysesTitleTooltip', {ns: 'analyses'})}
                                 className='dashboard-card'
                                 showMoreChevronLinkProps={{text: t('showAnalyses'), link: 'analyzy'}}>
                            <AnalysesNavList variant='compact'/>
                        </ContentSection>
                    </div>
                </>
            )}

            <ContentSection
                title={t('composition', {ns: 'translation', context: bodyType})}
                subtitle={t('compositionTooltip', {ns: 'translation'})}
                className='dashboard-card'
                showMoreChevronLinkProps={{link: 'zastupitele', text: t('showMembers')}}
                showMoreContentProps={{
                    condition: visibleMembersCount < (leaders.length > 0 ? leaders.length : filteredMembers.length),
                    onClick: () => setVisibleMembersCount(visibleMembersCount + membersIncrement)
                }}
            >
                {leader && !selectedCountry && (
                    <>
                        <h4>{capitalizeFirstLetter(basicData['leader_funkce'])}</h4>
                        <MemberCard memberData={leader}
                                    partyData={{partyName: leader.partyName, partyColor: leader.partyColor}}/>
                    </>
                )}
                <h4>{leaders.length > 0 && !selectedCountry ? t('leaders') : t('members', {ns: 'members'})}</h4>
                <div className='member-card-list compact'>
                    {filteredMembers
                        .slice(0, visibleMembersCount)
                        .map((member, index) => (
                            <MemberCard
                                key={index}
                                memberData={member}
                                partyData={{partyName: member.partyName, partyColor: member.partyColor}}
                            />
                        ))}
                </div>
            </ContentSection>
        </div>
    );
}

export default Home;
